import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Crew, CreateCrewDto, UpdateCrewDto, PaginatedResponse, CrewDTO, Profile, CrewModuleId, CREW_MODULE_REGISTRY, isModuleAvailableForCrew, isModuleEnabledForCrew } from '../../../shared/types';
import axiosService from '../services/axios.service';

export interface OnboardingData {
  crewName: string;
  websiteUrl?: string;
}

// Query key factory
export const crewKeys = {
    all: ['crews'] as const,
    lists: () => [...crewKeys.all, 'list'] as const,
    list: (page?: number, limit?: number) => [...crewKeys.lists(), { page, limit }] as const,
    details: () => [...crewKeys.all, 'detail'] as const,
    detail: (slug: string) => [...crewKeys.details(), slug] as const
};

// API functions
const crewApi = {
    getCrews: async (page: number = 1, limit: number = 10): Promise<PaginatedResponse<CrewDTO>> => {
        const { data } = await axiosService.get<PaginatedResponse<CrewDTO>>(`/c`, {
            params: { page, limit }
        });
        return data;
    },

    getCrew: async (slug: string): Promise<CrewDTO> => {
        const { data } = await axiosService.get<CrewDTO>(`/c/${slug}`);
        return data;
    },

    createCrew: async (crewData: CreateCrewDto): Promise<CrewDTO> => {
        const { data } = await axiosService.post<CrewDTO>(`/c`, crewData);
        return data;
    },

    updateCrew: async (slug: string, crewData: UpdateCrewDto): Promise<CrewDTO> => {
        const { data } = await axiosService.put<CrewDTO>(`/c/${slug}`, crewData);
        return data;
    },

    onboardUser: async (data: OnboardingData): Promise<{ crew: CrewDTO }> => {
        const crewData: CreateCrewDto = {
            name: data.crewName,
            websiteUrl: data.websiteUrl
        };

        const crew = await crewApi.createCrew(crewData);
        return { crew };
    }
};

// React Query hooks
export const useCrewInvitations = () => {
    return useQuery({
        queryKey: [...crewKeys.all, 'joinable'] as const,
        queryFn: async () => {
            const { data } = await axiosService.get<{ success: boolean; data: CrewDTO[] }>('/joinable');
            return data.data;
        }
    });
};

export const useCrews = (page: number = 1, limit: number = 10) => {
    return useQuery({
        queryKey: crewKeys.list(page, limit),
        queryFn: () => crewApi.getCrews(page, limit)
    });
};

export const useCrew = (slug: string) => {
    return useQuery({
        queryKey: crewKeys.detail(slug),
        queryFn: () => crewApi.getCrew(slug),
        enabled: !!slug,
        retry: (failureCount, error) => {
            // Retry only on network errors or 404s
            return error instanceof Error && (error.message.includes('Network Error') || error.message.includes('404'));
        }
    });
};

export const useIsHobbyCrew = (slug: string): boolean => {
    const { data: crew } = useCrew(slug);
    return crew?.crewType === 'hobby';
};

/**
 * Fully resolved module access for every module in the registry — registry
 * gate AND manual per-crew toggle AND dependency cascade, all folded in.
 * Supersedes ad hoc `!isHobbyCrew &&` checks: a module unavailable to this
 * crew's tier/crewType, disabled by a crew admin, or missing a dependency
 * all resolve to `false` here.
 */
export const useCrewModuleAccess = (slug: string): Record<CrewModuleId, boolean> => {
    const { data: crew } = useCrew(slug);
    return useMemo(() => {
        const entries = (Object.keys(CREW_MODULE_REGISTRY) as CrewModuleId[]).map(id => [
            id,
            // Prefer the server-resolved map (authoritative); fall back to computing
            // from the registry if an older payload doesn't carry moduleAccess yet.
            crew
                ? (crew.moduleAccess?.[id] ?? isModuleEnabledForCrew(id, crew))
                : false,
        ]);
        return Object.fromEntries(entries) as Record<CrewModuleId, boolean>;
    }, [crew]);
};

/** Fully resolved single-module check — see useCrewModuleAccess. */
export const useCrewModuleEnabled = (slug: string, moduleId: CrewModuleId): boolean =>
    useCrewModuleAccess(slug)[moduleId];

/** True if ANY of the given modules is enabled — for surfaces shared across modules. */
export const useAnyCrewModuleEnabled = (slug: string, moduleIds: CrewModuleId[]): boolean => {
    const access = useCrewModuleAccess(slug);
    return moduleIds.some(id => access[id]);
};

/**
 * Availability check: is this module part of the crew's tier plan OR granted by a
 * Persera override — ignoring the crew-admin preference toggle. Use for crew-admin
 * settings toggle-row visibility (a crew admin should see a toggle for anything
 * available to them), not for gating data fetches (use useCrewModuleEnabled).
 */
export const useModuleAvailable = (slug: string, moduleId: CrewModuleId): boolean => {
    const { data: crew } = useCrew(slug);
    return crew ? isModuleAvailableForCrew(moduleId, crew) : false;
};

export const useCrewAdmins = (slug: string) => {
    return useQuery({
        queryKey: [...crewKeys.detail(slug), 'admins'] as const,
        queryFn: async () => {
            const { data } = await axiosService.get<{ success: boolean; admins: Profile[]; total: number }>(
                `/c/${slug}/admins`
            );
            return data.admins;
        },
        enabled: !!slug
    });
};

export const useCreateCrew = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (crewData: CreateCrewDto) => crewApi.createCrew(crewData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: crewKeys.lists() });
        }
    });
};

export const useUpdateCrew = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ slug, data }: { slug: string; data: UpdateCrewDto }) =>
            crewApi.updateCrew(slug, data),
        onSuccess: (_, { slug }) => {
            queryClient.invalidateQueries({ queryKey: crewKeys.detail(slug) });
            queryClient.invalidateQueries({ queryKey: crewKeys.lists() });
        }
    });
};

export const useOnboardUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: OnboardingData) => crewApi.onboardUser(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: crewKeys.lists() });
        }
    });
};

// ============================================
// ADMIN HOOKS (Persera Admin Only)
// ============================================

/**
 * Get all crews for admin (no pagination)
 */
export const useAllCrewsAdmin = () => {
    return useQuery({
        queryKey: ['admin', 'crews'],
        queryFn: async () => {
            const { data } = await axiosService.get('/admin/crews');
            return data;
        }
    });
};
